import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "page-content",
  style: {"position":"relative"}
}
const _hoisted_2 = {
  key: 0,
  class: "table-overlay"
}
const _hoisted_3 = { class: "flex flex-wrap gap-3 m-3 align-items-center" }
const _hoisted_4 = { class: "flex align-items-center gap-3 flex-1" }
const _hoisted_5 = { class: "flex align-items-center" }
const _hoisted_6 = {
  class: "mr-2 pt-1",
  style: {"float":"left"}
}
const _hoisted_7 = ["textContent"]
const _hoisted_8 = { class: "justify-content-center" }
const _hoisted_9 = { class: "flex h-full justify-content-center" }
const _hoisted_10 = { class: "flex h-full justify-content-center" }
const _hoisted_11 = { class: "flex h-full justify-content-center" }
const _hoisted_12 = ["textContent"]
const _hoisted_13 = { class: "flex h-full justify-content-center" }

import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import InputSwitch from 'primevue/inputswitch';
import MultiSelect from 'primevue/multiselect';
import Menu from 'primevue/menu';
import ProgressSpinner from 'primevue/progressspinner';
import UserRemoveReferral from './components/UserRemoveReferral.vue';
import UserApproveReferral from './components/UserApproveReferral.vue';
import UserArchiveReferral from './components/UserArchiveReferral.vue';
import UserAutomatchReferral from './components/UserAutomatchReferral.vue';
import UserAssignToServiceProvider from './components/UserAssignToServiceProvider.vue';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { can, useDateTime, useRouting, useRequestCancel, useTenant } from '@/composables';
import { useStudentReferralStore, useCustomerSuccessPartnerStore } from '@/store';
import { UserReferralListFilterInterface, ReferralPayloadInterface, StudentClinicianInterface } from '@/types';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

const referralTimeout = 2000;

export default /*@__PURE__*/_defineComponent({
  __name: 'UserReferrals',
  setup(__props) {

const $routing = useRouting();
const $tenant = useTenant();
const referralStore = useStudentReferralStore();
const requestCancel = useRequestCancel();
const referralsSelected = ref<number[]>([]);
const selectedStudentNames = ref<Record<number, string>>({});
const allColumns = ref<boolean>(false);
const deleteId = ref(1);
const openApprove = ref<boolean>(false);
const openRemove = ref<boolean>(false);
const openAssign = ref<boolean>(false);
const openArchive = ref<boolean>(false);
const openAutomatch = ref<boolean>(false);
const payload = ref<ReferralPayloadInterface[]>([]);
const currentReferralApprovingId = ref<number>();
const noSPSelected = ref<number[]>([]);
const organizationId = ref<string | null>(null);
const filterKeys = ['organization_id', 'referral_type_id', 'search'];
const filters = ref<UserReferralListFilterInterface>({} as UserReferralListFilterInterface);
const globalServiceProvider = ref<StudentClinicianInterface | null>(null);
const customerSuccessPartnerStore = useCustomerSuccessPartnerStore();
const suppressOrgChange = ref(false);
const defaultFilters = {
  search: '',
  order: '',
  order_by: '',
  page: 1,
  per_page: 15,
  referral_type_id: '',
  organization_id: '',
  grade: '',
  discipline: '',
};

onMounted(async () => init());

onBeforeUnmount(() => {
  customerSuccessPartnerStore.selectedUsersSuccessPartners = [];
  requestCancel.requestSaverAbortAll();
});

const hasFilter = computed<boolean>(() => {
  let isFilter = false;
  Object.keys($routing.route.query).forEach((key) => {
    if (filterKeys.includes(key)) isFilter = true;
  });
  return isFilter;
});

const isUserList = computed<boolean>(
  () => $routing.route.name === 'user.list' || $routing.route.name === 'user.referrals',
);

const init = async () => {
  if (isUserList.value && can([PERMISSIONS.REFERRAL_ASSIGN])) {
    defineFilters();
    await referralStore.getStudents(filters.value);
    await referralStore.loadOptions();
    await referralStore.getOrganizations();
  }
};

$routing.routeChangeWatcher(async () => {
  defineFilters();
  await referralStore.getStudents(filters.value);
  await defineSelectAllReferrals();
});

// Check whether or not the current set of students displayed match
// the total previously selected in order to mark the select all
// checkbox as checked.
const defineSelectAllReferrals = async () => {
  const currentStudentIds = referralStore.students.data.map((item) => item.id);
  const allSelected: number[] = [];

  referralStore.students.data.forEach((item) => {
    const index = referralsSelected.value.findIndex((id) => id === item.id);
    if (index !== -1) allSelected.push(index);
  });

  selectAllReferrals.value = allSelected.length === currentStudentIds.length;
};

const defineFilters = async () => {
  const query = $routing.route.query;

  let orgIdValue = '';

  if (query.organization_id) {
    if (typeof query.organization_id === 'string') {
      orgIdValue = query.organization_id;
    } else if (Array.isArray(query.organization_id)) {
      orgIdValue = query.organization_id.join(',');
    }
  }

  filters.value = {
    search: query.search ? (query.search as string) : '',
    page: query.page ? parseInt(query.page as string) : '',
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
    referral_type_id: query.referral_type_id ? parseInt(query.referral_type_id as string) : '',
    organization_id: orgIdValue,
    order: query.order ? (query.order as string) : '',
    order_by: query.order_by ? (query.order_by as string) : '',
  };
};

const onClearFilters = () => {
  suppressOrgChange.value = true;
  filters.value = { ...(defaultFilters as UserReferralListFilterInterface) };
  organizationId.value = null;
  customerSuccessPartnerStore.selectedUsersSuccessPartners = [];
  $routing.removeAllQueryParam();
  setTimeout(() => {
    suppressOrgChange.value = false;
  }, 0);
};

const onReferralTypeChange = () => $routing.addQueryParam('referral_type_id', filters.value.referral_type_id as string);

const onOrganizationChange = () => {
  let selected = organizationId.value ? [organizationId.value] : [];

  const cspOrgIds = customerSuccessPartnerStore.selectedUsersSuccessPartners
    ? customerSuccessPartnerStore.selectedUsersSuccessPartners.flatMap((csp) => csp.organizations || [])
    : [];

  const merged = Array.from(new Set([...selected, ...cspOrgIds].filter(Boolean)));

  $routing.addQueryParam('organization_id', merged.join(','));

  filters.value.organization_id = merged;
  selectAllReferrals.value = false;
  referralsSelected.value = [];
};

const onApproveSave = () => {
  if (payload.value.length === 0) {
    referralStore.doToast(`Please select at least one referral to approve.`, '', true);
    return;
  }
  referralStore.approving = true;
  noSPSelected.value = [];
  payload.value.forEach(async (referral, index) => {
    if (referral.service_provider_id) {
      setTimeout(async () => {
        currentReferralApprovingId.value = referral.id;

        await referralStore.approveReferrals({
          id: referral.id,
          service_provider_id: referral.service_provider_id,
        });

        if (index === payload.value.length - 1) {
          resetReferralArray();
          setTimeout(() => {
            referralStore.approving = false;
            init();
          }, referralTimeout);
        }
      }, index * referralTimeout);
    } else {
      noSPSelected.value.push(referral.id);
      if (index === payload.value.length - 1) {
        resetReferralArray();
        setTimeout(() => {
          referralStore.approving = false;
          init();
        }, referralTimeout);
      }
    }
  });

  currentReferralApprovingId.value = undefined;
};

const onArchiveSave = () => {
  if (payload.value.length === 0) {
    referralStore.doToast(`Please select at least one student to archive.`, '', true);
    return;
  }

  referralStore.approving = true;

  payload.value.forEach(async (referral, index) => {
    setTimeout(async () => {
      currentReferralApprovingId.value = referral.id;
      await referralStore.archiveReferrals(referral.id, referral.student_id);

      if (index === payload.value.length - 1) {
        resetReferralArray();
        setTimeout(() => {
          referralStore.approving = false;
          init();
        }, referralTimeout);
      }
    }, index * referralTimeout);
  });

  currentReferralApprovingId.value = undefined;
};

const onAutomatchSave = () => {
  if (payload.value.length === 0) {
    referralStore.doToast(`Please select at least one referral to automatch.`, '', true);
    return;
  }

  referralStore.approving = true;
  noSPSelected.value = [];

  payload.value.forEach(async (referral, index) => {
    setTimeout(async () => {
      currentReferralApprovingId.value = referral.id;
      await referralStore.automatchReferrals(referral.id);

      if (index === payload.value.length - 1) {
        resetReferralArray();
        setTimeout(() => {
          referralStore.approving = false;
          init();

          // Keep track of the referal id's that have not been automatched.
          if (referralStore.unApprovedStudentIds?.length) {
            referralStore.unApprovedStudentIds.forEach((item) => {
              referralsSelected.value.push(item);
            });
          }
        }, referralTimeout);
      }
    }, index * referralTimeout);
  });

  currentReferralApprovingId.value = undefined;
};

const resetReferralArray = () => {
  setTimeout(() => {
    payload.value = [];
    referralsSelected.value = [...noSPSelected.value];
    currentReferralApprovingId.value = undefined;
  }, referralTimeout);
};

const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('search');
  }
};

const onPage = (event: DataTablePageEvent) => {
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });

  referralStore.students.data.forEach((student) => {
    if (referralsSelected.value.includes(student.id)) {
      student.selected = true;
      selectedStudentNames.value[student.id] = student.student_name;
    }
  });
  formatSelectedStudentNamesAndSetPayload();
};

const onSort = (event: DataTableSortEvent) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  const field = event.sortField;
  filters.value.order = order;
  filters.value.order_by = field as string;
  $routing.addQueryParams({ order, order_by: field });
};

const formattedStudentNames = ref('');

const openModal = (modal: 'approve' | 'remove' | 'assign' | 'archive' | 'automatch') => {
  if (modal === 'approve') {
    formatSelectedStudentNamesAndSetPayload();
    openApprove.value = true;
  }

  if (modal === 'automatch') {
    formatSelectedStudentNamesAndSetPayload();
    openAutomatch.value = true;
  }

  if (modal === 'archive') {
    formatSelectedStudentNamesAndSetPayload();
    openArchive.value = true;
  }

  if (modal === 'remove') openRemove.value = true;
  if (modal === 'assign') openAssign.value = true;
};

const closeModal = (modal: 'approve' | 'remove' | 'assign' | 'archive' | 'automatch') => {
  if (modal === 'approve') openApprove.value = false;
  if (modal === 'remove') openRemove.value = false;
  if (modal === 'assign') openAssign.value = false;
  if (modal === 'archive') openArchive.value = false;
  if (modal === 'automatch') openAutomatch.value = false;
};

const assignStudentToServiceProvider = (serviceProvider: StudentClinicianInterface) => {
  globalServiceProvider.value = serviceProvider;
  const ids = referralsSelected.value;

  if (ids.length) {
    ids.forEach((id) => {
      // Update current page students for immediate UI update
      referralStore.students.data.forEach((student) => {
        if (student.id === id) {
          student.service_provider_id = serviceProvider.id;
          student.service_provider_name = serviceProvider.display_name;
        }
      });

      // Update payload for persistence
      let found = payload.value.find((item) => item.id === id);

      if (found) {
        found.service_provider_id = serviceProvider.id;
      } else {
        // Optionally add to payload if not present
        const student = referralStore.students.data.find((s) => s.id === id);

        if (student) {
          payload.value.push({
            id: student.id,
            service_provider_id: serviceProvider.id,
            index: student.index,
            student_name: student.student_name,
            page: filters.value.page,
          });
        }
      }
    });
  }
};

const formatSelectedStudentNamesAndSetPayload = () => {
  const ids = referralsSelected.value;
  if (ids.length) {
    const nameArray: string[] = [];

    ids.forEach((id) => {
      const name = selectedStudentNames.value[id]; // Use the mapping for names
      if (name) {
        nameArray.push(name);
      }

      const found = referralStore.students.data.find((student) => student.id === id);
      const studentIndex = referralStore.students.data.findIndex((student) => student.id === id);

      const inPayload = payload.value.find((item) => item.id === id);

      if (!filters.value.page) {
        filters.value.page = 1;
      }

      if (!inPayload && found) {
        payload.value.push({
          id: found.id,
          service_provider_id: found.service_provider_id,
          index: studentIndex,
          student_id: found.student_id,
          student_name: found.student_name,
          page: filters.value.page,
        });
      }
    });

    payload.value = Object.values(payload.value).sort((a, b) => (a.index > b.index ? 1 : -1));

    formattedStudentNames.value = nameArray.join(', ');
  }
};

const referredSuccessfully = (id: number) => referralStore.approvedStudentIds?.includes(id);

const handleCheckboxChange = async (checked: boolean, id: number, name: string) => {
  if (checked) {
    selectedStudentNames.value[id] = name;
  } else {
    delete selectedStudentNames.value[id];
  }

  if (referralStore.unApprovedStudentIds?.length) {
    const index = referralStore.unApprovedStudentIds.findIndex((item) => item === id);

    if (index > -1) {
      referralStore.unApprovedStudentIds.splice(index, 1);
    }
  }

  await defineSelectAllReferrals();
};

const updateReferrals = (updatedReferrals: ReferralPayloadInterface[]) => {
  payload.value = updatedReferrals;
  referralsSelected.value = updatedReferrals.map((referral) => referral.id);
};

const handleDropdownChange = (serviceProviderId: number, student: ReferralPayloadInterface) => {
  let found = payload.value.find((item) => item.id === student.id);
  if (found) {
    found.service_provider_id = serviceProviderId.value;
  } else {
    payload.value.push({
      id: student.id,
      service_provider_id: serviceProviderId.value,
      index: student.index,
      student_name: student.student_name,
      page: filters.value.page,
    });
  }
};

watch(
  () => referralStore.students?.data || [],
  (students) => {
    if (students?.length) {
      students.forEach((student) => {
        const savedReferral = payload.value.find((item) => item.id === student.id);
        if (savedReferral) {
          student.service_provider_id = savedReferral.service_provider_id;
        }
      });
    }
  },
  { immediate: true },
);

watch(
  () => customerSuccessPartnerStore.selectedUsersSuccessPartners,
  () => {
    if (!suppressOrgChange.value) {
      onOrganizationChange();
    }
  },
  { deep: true },
);

const actions = ref();
const selectAllReferrals = ref();

const actionItems = (() => {
  return [
    {
      label: 'Approve',
      icon: {
        name: 'add',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('approve'),
    },
    {
      label: 'Archive',
      icon: {
        name: 'vpn_key',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('archive'),
    },
    {
      label: 'Assign to Service Provider',
      icon: {
        name: 'assignment',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('assign'),
    },
    {
      label: 'Auto Match',
      icon: {
        name: 'assignment',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('automatch'),
      visible:
        $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_SP_SCHOOL_AUTOMATCHING) &&
        $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_AUTOMATCHING_FOR_UNASSIGNED) &&
        $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_AUTOMATCHING_FOR_REFERRALS),
    },
  ];
})();

const onActionsToggle = (event: Event) => actions.value.toggle(event);

const onSelectAllReferrals = () => {
  if (selectAllReferrals.value) {
    referralStore.students.data.forEach((item) => {
      const exists = referralsSelected.value.find((id) => id === item.id);

      if (!exists) {
        referralsSelected.value.push(item.id);
        selectedStudentNames.value[item.id] = item.student_name;
      }
    });
  } else {
    referralStore.students.data.forEach((item) => {
      const index = referralsSelected.value.findIndex((id) => id === item.id);

      if (index !== -1) {
        delete referralsSelected.value[index];
        delete selectedStudentNames.value[item.id];
      }
    });
  }
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Checkbox = _resolveComponent("Checkbox")!
  const _component_Column = _resolveComponent("Column")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_debounce = _resolveDirective("debounce")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(UserRemoveReferral, {
      id: deleteId.value,
      open: openRemove.value,
      onUpdate: init,
      onCancel: _cache[0] || (_cache[0] = ($event: any) => (closeModal('remove')))
    }, null, 8, ["id", "open"]),
    _createVNode(UserApproveReferral, {
      referrals: payload.value,
      open: openApprove.value,
      onSave: onApproveSave,
      onCancel: _cache[1] || (_cache[1] = ($event: any) => (closeModal('approve'))),
      onUpdateReferrals: updateReferrals
    }, null, 8, ["referrals", "open"]),
    _createVNode(UserArchiveReferral, {
      referrals: payload.value,
      open: openArchive.value,
      onSave: onArchiveSave,
      onCancel: _cache[2] || (_cache[2] = ($event: any) => (closeModal('archive'))),
      onUpdateReferrals: updateReferrals
    }, null, 8, ["referrals", "open"]),
    _createVNode(UserAutomatchReferral, {
      referrals: payload.value,
      open: openAutomatch.value,
      onSave: onAutomatchSave,
      onCancel: _cache[3] || (_cache[3] = ($event: any) => (closeModal('automatch'))),
      onUpdateReferrals: updateReferrals
    }, null, 8, ["referrals", "open"]),
    (openAssign.value && _unref(can)([_unref(PERMISSIONS).REFERRAL_ASSIGN]))
      ? (_openBlock(), _createBlock(UserAssignToServiceProvider, {
          key: 0,
          ids: referralsSelected.value,
          onSave: assignStudentToServiceProvider,
          onCancel: _cache[4] || (_cache[4] = ($event: any) => (closeModal('assign')))
        }, null, 8, ["ids"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      (_unref(referralStore).studentsLoading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_unref(ProgressSpinner), { style: {"width":"50px","height":"50px"} })
          ]))
        : _createCommentVNode("", true),
      (_unref(can)([_unref(PERMISSIONS).REFERRAL_ASSIGN]))
        ? (_openBlock(), _createBlock(_component_Card, { key: 1 }, {
            content: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(_component_IconField, { "icon-position": "left" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_InputIcon, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_Icon, {
                            name: "search",
                            class: "text-lg"
                          })
                        ]),
                        _: 1
                      }),
                      _withDirectives(_createVNode(_component_InputText, {
                        modelValue: filters.value.search,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.value.search) = $event)),
                        disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                        class: "w-full min-w-40",
                        style: {"min-width":"200px"},
                        placeholder: "Search by Student Name"
                      }, null, 8, ["modelValue", "disabled"]), [
                        [_directive_debounce, onSearch, "600ms"]
                      ])
                    ]),
                    _: 1
                  }),
                  (_unref(referralStore).loading)
                    ? (_openBlock(), _createBlock(_component_Skeleton, {
                        key: 0,
                        height: "36px",
                        width: "160px",
                        "border-radius": "4"
                      }))
                    : (_openBlock(), _createBlock(_component_Dropdown, {
                        key: 1,
                        id: "organizations",
                        modelValue: organizationId.value,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((organizationId).value = $event)),
                        filter: "",
                        disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                        type: "secondary",
                        options: _unref(referralStore).organizations,
                        "option-label": "name",
                        "option-value": "id",
                        placeholder: "Filter by Organization",
                        style: {"width":"200px","max-width":"200px"},
                        onChange: onOrganizationChange
                      }, null, 8, ["modelValue", "disabled", "options"])),
                  (_unref(referralStore).loading)
                    ? (_openBlock(), _createBlock(_component_Skeleton, {
                        key: 2,
                        height: "36px",
                        width: "160px",
                        "border-radius": "4"
                      }))
                    : (_openBlock(), _createBlock(_component_Dropdown, {
                        key: 3,
                        id: "referral_types",
                        modelValue: filters.value.referral_type_id,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((filters.value.referral_type_id) = $event)),
                        disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                        type: "secondary",
                        options: _unref(referralStore).referralTypes,
                        "option-label": "title",
                        "option-value": "id",
                        placeholder: "Filter by Referral Type",
                        style: {"width":"200px","max-width":"200px"},
                        onChange: onReferralTypeChange
                      }, null, 8, ["modelValue", "disabled", "options"])),
                  (_unref($tenant).tenantSettingEnabled(_unref(TENANT_SETTINGS).UTILIZATION_TRACKING))
                    ? (_openBlock(), _createBlock(_unref(MultiSelect), {
                        key: 4,
                        modelValue: _unref(customerSuccessPartnerStore).selectedUsersSuccessPartners,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(customerSuccessPartnerStore).selectedUsersSuccessPartners) = $event)),
                        options: _unref(customerSuccessPartnerStore).customerSuccessPartners,
                        "option-label": "display_name",
                        "max-selected-labels": 2,
                        loading: _unref(customerSuccessPartnerStore).loading,
                        disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                        placeholder: "All CSPs"
                      }, null, 8, ["modelValue", "options", "loading", "disabled"]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(_unref(InputSwitch), {
                        modelValue: allColumns.value,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((allColumns).value = $event)),
                        disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading
                      }, null, 8, ["modelValue", "disabled"])
                    ]),
                    _cache[14] || (_cache[14] = _createElementVNode("div", {
                      class: "w-4rem text-gray-600 font-normal line-height-1 align-items-center",
                      style: {"float":"left"}
                    }, [
                      _createElementVNode("label", { class: "text-xs mt-1" }, "Show All Columns")
                    ], -1))
                  ]),
                  _createElementVNode("div", null, [
                    (hasFilter.value)
                      ? (_openBlock(), _createBlock(_component_AppButton, {
                          key: 0,
                          type: "secondary",
                          text: "Clear Filters",
                          "icon-left": "clear",
                          disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                          onClick: onClearFilters
                        }, null, 8, ["disabled"]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createElementVNode("div", null, [
                  _createVNode(_component_AppButton, {
                    type: "secondary",
                    text: "Actions",
                    "icon-left": "more_horiz",
                    "aria-haspopup": "true",
                    "aria-controls": "referral_actions",
                    disabled: referralsSelected.value.length === 0 || _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                    onClick: onActionsToggle
                  }, null, 8, ["disabled"]),
                  _createVNode(_unref(Menu), {
                    id: "referral_actions",
                    ref_key: "actions",
                    ref: actions,
                    class: "mt-2 ml-2 p-2 w-max",
                    model: _unref(actionItems),
                    popup: true
                  }, null, 8, ["model"])
                ])
              ]),
              _createVNode(_component_DataTable, {
                filters: filters.value,
                "onUpdate:filters": _cache[12] || (_cache[12] = ($event: any) => ((filters).value = $event)),
                selection: referralsSelected.value,
                "onUpdate:selection": _cache[13] || (_cache[13] = ($event: any) => ((referralsSelected).value = $event)),
                "resizable-columns": true,
                "column-resize-mode": "fit",
                "filter-display": "menu",
                value: _unref(referralStore).students.data,
                paginator: true,
                class: _normalizeClass(["p-datatable-customers", { 'table-loading': _unref(referralStore).studentsLoading }]),
                rows: filters.value.per_page,
                "total-records": _unref(referralStore).students.total,
                "data-key": "id",
                "loading-icon": "logo-spinner",
                loading: _unref(referralStore).studentsLoading,
                "rows-per-page-options": [15, 25, 30],
                "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
                "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
                "responsive-layout": "scroll",
                lazy: true,
                onPage: onPage,
                onSort: onSort
              }, {
                empty: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                  _createTextVNode("No referrals found", -1)
                ]))]),
                default: _withCtx(() => [
                  (!_unref(referralStore).studentsLoading && _unref(referralStore).students.total)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createVNode(_component_Column, {
                          class: "px-2",
                          "header-class": "w-0"
                        }, {
                          header: _withCtx(() => [
                            _createVNode(_component_Checkbox, {
                              modelValue: selectAllReferrals.value,
                              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((selectAllReferrals).value = $event)),
                              binary: "",
                              value: true,
                              onChange: onSelectAllReferrals
                            }, null, 8, ["modelValue"])
                          ]),
                          body: _withCtx(({ data }) => [
                            (_unref(referralStore).approving && currentReferralApprovingId.value === data.id)
                              ? (_openBlock(), _createBlock(_component_Icon, {
                                  key: 0,
                                  name: "cached",
                                  class: "text-primary spin-cached"
                                }))
                              : (referredSuccessfully(data.id))
                                ? (_openBlock(), _createBlock(_component_Icon, {
                                    key: 1,
                                    name: "check_circle",
                                    class: "text-primary"
                                  }))
                                : (_openBlock(), _createBlock(_component_Checkbox, {
                                    key: 2,
                                    modelValue: referralsSelected.value,
                                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((referralsSelected).value = $event)),
                                    disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                                    value: data.id,
                                    onChange: (checked) => handleCheckboxChange(checked, data.id, data.student_name)
                                  }, null, 8, ["modelValue", "disabled", "value", "onChange"]))
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "student_id",
                          header: "SSID",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", {
                              class: "flex h-full justify-content-center text-center",
                              textContent: _toDisplayString(data.student_external_id ?? 'N/A')
                            }, null, 8, _hoisted_7)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "student",
                          header: "Student",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", _hoisted_8, [
                              _createVNode(_component_RouterLink, {
                                to: `/user/${data.student_id}`,
                                target: "_blank",
                                class: "font-semibold",
                                textContent: _toDisplayString(data.student_name)
                              }, null, 8, ["to", "textContent"])
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "service_provider",
                          header: "Service Provider",
                          sortable: true
                        }, {
                          body: _withCtx(({ data }) => [
                            _createVNode(_component_Dropdown, {
                              modelValue: data.service_provider_id,
                              "onUpdate:modelValue": ($event: any) => ((data.service_provider_id) = $event),
                              type: "secondary",
                              options: _unref(referralStore).getStudentOrganizationServiceProviders(data.organization_id),
                              "option-label": "display_name",
                              "option-value": "id",
                              placeholder: "Service Provider",
                              disabled: _unref(referralStore).approving || _unref(referralStore).studentsLoading,
                              onChange: (value) => handleDropdownChange(value, data)
                            }, null, 8, ["modelValue", "onUpdate:modelValue", "options", "disabled", "onChange"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "organization",
                          header: "Organization",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createVNode(_component_RouterLink, {
                              to: `/organizations/${data.organization_id}`,
                              target: "_blank",
                              class: "font-semibold",
                              textContent: _toDisplayString(data.organization_name)
                            }, null, 8, ["to", "textContent"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "building_campus",
                          header: "Building/Campus",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", _hoisted_9, [
                              _createElementVNode("span", null, _toDisplayString(data.student_building_campus ?? 'N/A'), 1)
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "grade",
                          header: "Grade",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", _hoisted_10, [
                              _createElementVNode("span", null, _toDisplayString(data.grade), 1)
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "discipline",
                          header: "Discipline",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", _hoisted_11, [
                              _createElementVNode("span", null, [
                                (Array.isArray(data.treatment_service_discipline))
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      (data.treatment_service_discipline.length > 1)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createTextVNode(_toDisplayString(data.treatment_service_discipline.map((d) => d.code || d.name || d).join(', ')), 1)
                                          ], 64))
                                        : (data.treatment_service_discipline.length === 1)
                                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                              _createTextVNode(_toDisplayString(data.treatment_service_discipline[0].name ||
                          data.treatment_service_discipline[0].code ||
                          data.treatment_service_discipline[0]), 1)
                                            ], 64))
                                          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                              _createTextVNode("N/A")
                                            ], 64))
                                    ], 64))
                                  : (
                        data.treatment_service_discipline && typeof data.treatment_service_discipline === 'object'
                      )
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createTextVNode(_toDisplayString(data.treatment_service_discipline.name || data.treatment_service_discipline.code || 'N/A'), 1)
                                      ], 64))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                        _createTextVNode(_toDisplayString(data.treatment_service_discipline ?? 'N/A'), 1)
                                      ], 64))
                              ])
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "referred_at",
                          header: "Date of Referral",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", {
                              class: "flex h-full justify-content-center",
                              textContent: _toDisplayString(_unref(useDateTime)().formatIntlDate(data.referred_at))
                            }, null, 8, _hoisted_12)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "reason",
                          header: "Reason",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("span", null, _toDisplayString(data.reason ?? 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "days_since_service",
                          header: "Days Since Last Service",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", _hoisted_13, [
                              _createElementVNode("span", null, _toDisplayString(data.days_since_last_service), 1)
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "referral_type",
                          header: "Referral Type",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("span", null, _toDisplayString(data.referral_type_title ?? 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "referral_reason",
                          header: "Referral Reason",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("span", null, _toDisplayString(data.referral_reason_title ?? 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "referral_urgency",
                          header: "Urgency",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("span", null, _toDisplayString(data.referral_urgency_title ?? 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        (allColumns.value)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createVNode(_component_Column, {
                                field: "treatment_type",
                                header: "Plan Type",
                                sortable: ""
                              }, {
                                body: _withCtx(({ data }) => [
                                  _createElementVNode("span", null, _toDisplayString(data.treatment_type_name ?? 'N/A'), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_Column, {
                                field: "treatment_category",
                                header: "Service Category",
                                sortable: ""
                              }, {
                                body: _withCtx(({ data }) => [
                                  _createElementVNode("span", null, _toDisplayString(data.treatment_category_name ?? 'N/A'), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_Column, {
                                field: "treatment_service",
                                header: "Service",
                                sortable: ""
                              }, {
                                body: _withCtx(({ data }) => [
                                  _createElementVNode("span", null, _toDisplayString(data.treatment_service_discipline ?? 'N/A'), 1)
                                ]),
                                _: 1
                              })
                            ], 64))
                          : _createCommentVNode("", true)
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["filters", "selection", "value", "class", "rows", "total-records", "loading"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ])
  ], 64))
}
}

})